import { ProductCategory } from "@/core/common/types";
import { VideoCameraMotion } from "@/core/common/types/video";

/**
 * Creates customized video prompt instructions based on product category, camera motion, and keyframes
 */
export function createVideoPromptInstructions({
  category,
  cameraMotion,
  keyframePrompt,
  customPrompt,
}: {
  category: ProductCategory;
  cameraMotion?: VideoCameraMotion;
  keyframePrompt: string;
  customPrompt?: string;
}): string {
  // If a custom prompt is provided, use it instead of the category-specific instructions
  if (customPrompt) {
    return formatPrompt(customPrompt, keyframePrompt, cameraMotion);
  }

  // Otherwise, get category-specific movement instructions
  const categoryInstructions = getCategorySpecificInstructions(category);

  // Format the prompt with all parameters
  return formatPrompt(categoryInstructions, keyframePrompt, cameraMotion);
}

/**
 * Returns category-specific movement and styling instructions for the video
 */
function getCategorySpecificInstructions(category: ProductCategory): string {
  switch (category) {
    case ProductCategory.Fashion:
      return "Include subtle human movements like shifting side to side, small posture changes, slight head tilts, or fabric movement. The model should look natural with small breathing movements and subtle expressions.";

    case ProductCategory.Beauty:
      return "Focus on light reflecting off surfaces with subtle movements. If showing a person, include natural facial expressions, slight head turns to showcase the product, and gentle hand gestures interacting with the product.";

    case ProductCategory.Jewelry:
      return "Capture light reflecting and refracting through gemstones and metals. If jewelry is worn by a person, show them slightly turning to let light catch different facets. Include subtle movements like a hand touching the jewelry or adjusting it.";

    case ProductCategory.Watches:
      return "Showcase the watch with detailed movement that highlights its features. Include subtle wrist turns or rotations to reveal different angles. Focus on reflections on the watch face and any moving parts.";

    case ProductCategory.Bags:
      return "Show the bag with subtle movements that reveal its texture and form. If on a person, include natural walking or carrying motions. Focus on details like straps moving, clasps opening/closing, or fabric shifting.";

    case ProductCategory.Shoes:
      return "Capture subtle movements that show different angles of the footwear. If worn by a person, include natural stepping or walking motions. Focus on how the material flexes and how light plays on different surfaces.";

    case ProductCategory.Furniture:
      return "Create a scene with subtle environmental movements like curtains billowing, plants swaying, or natural light shifting. The furniture should be the static focal point surrounded by gentle movement.";

    case ProductCategory.HomeDecor:
      return "Show the decor item with subtle environmental context like soft shadows moving, reflections changing, or nearby elements (curtains, plants) having gentle movement. Focus on how the item fits within its space.";

    case ProductCategory.Food:
      return "Include gentle steam rising, subtle movements of garnishes, or liquids flowing/settling. Consider natural environmental movements like a gentle breeze affecting the presentation or people interacting with the food in the background.";

    case ProductCategory.Technology:
      return "Show the tech product with subtle interface animations, blinking lights, or screens changing. If applicable, include gentle human interaction like fingers tapping or hands gesturing around the device.";

    default:
      return "Include natural, subtle movements that highlight the product's features. Focus on environmental elements like gentle lighting shifts, subtle background movements, or soft interactions that emphasize the product.";
  }
}

/**
 * Formats the final prompt with all parameters
 */
function formatPrompt(
  categoryInstructions: string,
  keyframePrompt: string,
  cameraMotion?: VideoCameraMotion,
): string {
  if (cameraMotion) {
    return `Describe in detail a video ${keyframePrompt}. Make sure the camera motion is ${cameraMotion}. ${categoryInstructions} Describe all the subject, surroundings, background, lighting, movements, and camera motion concisely. Start the caption with 'A video of'. Return plain object of type {"caption": string}`;
  } else {
    return `Describe in detail a video ${keyframePrompt}. ${categoryInstructions} Describe all the subject, surroundings, background, lighting, movements, and camera motion concisely. Start the caption with 'A video of'. Also pick the most suitable camera motion from this list: ['Move Left', 'Move Right', 'Move Up', 'Move Down', 'Push In', 'Pull Out', 'Zoom In', 'Zoom Out', 'Pan Left', 'Pan Right', 'Orbit Left', 'Orbit Right', 'Crane Up', 'Crane Down']. Return plain object of type {"caption": string, "camera_motion": string}`;
  }
}
